import { Button, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AIRPLX_TOKEN } from "../../containers/IdentityContainer";
import { useTokenMaker } from "../../hooks";
import { getRedirectUrl } from "../../utils/redirectManager";
import { createUnauthenticatedPostgrest } from "../../utils/unauthenticatedApi";
import { Loading } from "../../widgets/Loading";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const VerifyEmail: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { url: tokenMakerURL } = useTokenMaker();

  const [loading, setLoading] = React.useState<boolean>(Boolean(code));
  const [error, setError] = React.useState<string>("");
  const [email] = React.useState<string>(location.state?.email || "");
  const [resending, setResending] = React.useState<boolean>(false);

  const postgrest = React.useMemo(() => {
    return createUnauthenticatedPostgrest();
  }, []);

  // Auto-verify if code is in URL
  React.useEffect(() => {
    if (code) {
      handleVerify(code);
    }
  }, [code]);

  const handleVerify = async (verificationCode: string) => {
    setLoading(true);
    setError("");

    try {
      const { data: token } = await postgrest.rpc("verify_email_with_code", {
        p_code: verificationCode,
      });

      if (!token) {
        setError(t("screens.verifyEmail.errors.invalidOrExpired"));
        setLoading(false);
        return;
      }

      // Store token (auto-login)
      localStorage.setItem(AIRPLX_TOKEN, token as unknown as string);

      // Redirect to the stored URL or default to home page
      // Set hash then force reload (browser optimizes same-origin hash changes without reload)
      const redirectUrl = getRedirectUrl("/");
      window.location.href = `/#${redirectUrl}`;
      window.location.reload();
    } catch (err: any) {
      setError(err.message || t("screens.verifyEmail.errors.genericError"));
      console.error("Error verifying email:", err);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError(t("screens.verifyEmail.errors.noEmail"));
      return;
    }

    setResending(true);
    setError("");

    try {
      await fetch(`${tokenMakerURL}/send-verification-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      // Show success message (could add a success state)
      setError(""); // Clear any previous errors
    } catch (err) {
      setError(t("screens.verifyEmail.errors.resendFailed"));
      console.error("Error resending verification:", err);
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        width="100vw"
      >
        <Stack direction="column" width={450} spacing={3}>
          {error ? (
            <>
              <Stack spacing={1} alignItems="center">
                <Typography variant="h5">{t("screens.verifyEmail.failureTitle")}</Typography>
                <Typography variant="body1" color="error" align="center">
                  {error}
                </Typography>
              </Stack>

              {email && (
                <Button
                  variant="contained"
                  onClick={handleResend}
                  disabled={resending}
                  fullWidth
                  sx={{ borderRadius: 1 }}
                >
                  {resending ? t("screens.verifyEmail.resendingButton") : t("screens.verifyEmail.resendButton")}
                </Button>
              )}

              <Button variant="outlined" onClick={() => navigate("/login")}>
                {t("screens.verifyEmail.backToLogin")}
              </Button>
            </>
          ) : (
            <>
              <Stack spacing={1} alignItems="center">
                <Typography variant="h5">{t("screens.verifyEmail.successTitle")}</Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                >
                  {t("screens.verifyEmail.successMessage")}
                  <br />
                  <strong>{email}</strong>
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary" align="center">
                {t("screens.verifyEmail.instructions")}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
              >
                {t("screens.verifyEmail.expiryNote")}
              </Typography>

              <Button
                variant="outlined"
                onClick={handleResend}
                disabled={resending}
                fullWidth
              >
                {resending ? t("screens.verifyEmail.resendingButton") : t("screens.verifyEmail.resendButton")}
              </Button>

              <Button size="small" onClick={() => navigate("/login")}>
                {t("screens.verifyEmail.backToLogin")}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
